import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { format, parseISO } from "date-fns";
import type { MapPin, PinCategory, TripData } from "../types";
import { CategoryIcon } from "./StatusBadge";
import { PlaceDetail } from "./PlaceDetail";
import { pinMayHavePhoto } from "../lib/googlePlaces";
import { computeDayQuest } from "../lib/questMapState";
import { arrowIcon, makeIcon } from "./mapMarkers";
import {
  appleDayMapsUrl,
  googleDayMapsUrl,
  mapsLinks,
  prefersAppleMaps,
} from "../lib/mapsLinks";
import "leaflet/dist/leaflet.css";

const FILTERS: { id: PinCategory | "all"; label: string }[] = [
  { id: "all", label: "All" },
  { id: "lodging", label: "Lodging" },
  { id: "transport", label: "Transport" },
  { id: "attraction", label: "Attractions" },
  { id: "food", label: "Food" },
  { id: "coffee", label: "☕ Coffee" },
  { id: "flight", label: "Flights" },
];

function FitBounds({
  pins,
  selectedDate,
}: {
  pins: MapPin[];
  selectedDate: string | null;
}) {
  const map = useMap();
  useEffect(() => {
    const focus =
      selectedDate != null
        ? pins.filter((p) => p.dayIds?.includes(selectedDate))
        : pins;
    const target = focus.length > 0 ? focus : pins;
    if (target.length === 0) return;
    const bounds = L.latLngBounds(
      target.map((p) => [p.lat, p.lng] as [number, number]),
    );
    map.fitBounds(bounds.pad(0.25), { maxZoom: 14 });
  }, [map, pins, selectedDate]);
  return null;
}

interface Props {
  data: TripData;
  selectedDate: string | null;
  /** Selected quest on the selected day. Absent → first item is active. */
  selectedItemId?: string | null;
  onSelectDate: (date: string) => void;
}

export function MapView({
  data,
  selectedDate,
  selectedItemId = null,
  onSelectDate,
}: Props) {
  const [filter, setFilter] = useState<PinCategory | "all">("all");
  const [dayOnly, setDayOnly] = useState(true);

  /** Coffee filter dims non-coffee instead of hiding them. */
  const coffeeDimMode = filter === "coffee";

  const mapPins = useMemo(() => {
    if (coffeeDimMode) return data.pins;
    return filter === "all"
      ? data.pins
      : data.pins.filter((p) => p.category === filter);
  }, [data.pins, filter, coffeeDimMode]);

  const visiblePins = useMemo(() => {
    let list = mapPins;
    if (dayOnly && selectedDate) {
      list = list.filter((p) => p.dayIds?.includes(selectedDate));
    }
    if (coffeeDimMode) {
      return [...list].sort((a, b) => {
        const ac = a.category === "coffee" ? 0 : 1;
        const bc = b.category === "coffee" ? 0 : 1;
        return ac - bc;
      });
    }
    return list;
  }, [mapPins, dayOnly, selectedDate, coffeeDimMode]);

  const quest = useMemo(() => {
    if (!selectedDate) {
      return { pinState: new Map(), arrow: null, activePin: null };
    }
    const day = data.days.find((d) => d.date === selectedDate);
    if (!day) {
      return { pinState: new Map(), arrow: null, activePin: null };
    }
    return computeDayQuest(day.items, data.pins, selectedItemId);
  }, [data.days, data.pins, selectedDate, selectedItemId]);

  const dayOpenLinks = useMemo(() => {
    if (!selectedDate) return null;
    const dayPins = data.pins.filter((p) => p.dayIds?.includes(selectedDate));
    const day = data.days.find((d) => d.date === selectedDate);
    const label =
      day?.location ?? day?.title ?? `Chile trip ${selectedDate}`;
    const google = googleDayMapsUrl(dayPins, label);
    const apple = appleDayMapsUrl(dayPins, label);
    const appleFirst = prefersAppleMaps();
    return appleFirst
      ? [
          { href: apple, label: "Open this day in Apple Maps", key: "apple" },
          { href: google, label: "Open this day in Google Maps", key: "google" },
        ]
      : [
          { href: google, label: "Open this day in Google Maps", key: "google" },
          { href: apple, label: "Open this day in Apple Maps", key: "apple" },
        ];
  }, [selectedDate, data.pins, data.days]);

  const mapCenter: [number, number] = [-51.0, -72.95];

  return (
    <div className="map-pane">
      <section className="card map-toolbar">
        <header className="panel-header">
          <h2>Map</h2>
          <span className="muted">
            {selectedDate
              ? format(parseISO(selectedDate), "MMM d")
              : "OpenStreetMap"}
          </span>
        </header>
        <div className="filter-row">
          <button
            type="button"
            className={`chip ${dayOnly ? "active" : ""}`}
            onClick={() => setDayOnly((v) => !v)}
            title="Highlight pins for the selected day"
          >
            {dayOnly ? "Day pins" : "All days"}
          </button>
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              className={`chip ${filter === f.id ? "active" : ""}${f.id === "coffee" ? " chip-coffee" : ""}`}
              onClick={() => setFilter(f.id)}
              aria-pressed={filter === f.id}
            >
              {f.label}
            </button>
          ))}
        </div>
        {coffeeDimMode && (
          <p className="coffee-filter-hint muted">
            Coffee filter on — non-coffee pins are dimmed.
          </p>
        )}
        {dayOpenLinks && (
          <div
            className="maps-area-actions day-maps-open"
            role="group"
            aria-label="Open selected day in native maps"
          >
            {dayOpenLinks.map((l) => (
              <a
                key={l.key}
                className={`maps-chip maps-chip-${l.key}`}
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={l.label}
              >
                {l.label}
              </a>
            ))}
          </div>
        )}
      </section>

      <div className="map-frame card">
        <MapContainer
          center={mapCenter}
          zoom={8}
          scrollWheelZoom
          className="leaflet-root"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FitBounds
            pins={
              coffeeDimMode
                ? mapPins.filter((p) => p.category === "coffee")
                : mapPins
            }
            selectedDate={dayOnly ? selectedDate : null}
          />
          {mapPins.map((p) => {
            const isDay =
              selectedDate != null &&
              (p.dayIds?.includes(selectedDate) ?? false);
            const visit = quest.pinState.get(p.id) ?? "unvisited";
            const dayDimmed = Boolean(dayOnly && selectedDate && !isDay);
            const coffeeDimmed =
              coffeeDimMode && p.category !== "coffee" && visit !== "active";
            const dimmed = dayDimmed || coffeeDimmed;
            return (
              <Marker
                key={`${p.id}-${visit}`}
                title={p.name}
                alt={p.name}
                position={[p.lat, p.lng]}
                icon={makeIcon(p.category, dimmed, visit)}
                opacity={dimmed ? 0.4 : 1}
                zIndexOffset={
                  visit === "active"
                    ? 1000
                    : p.category === "coffee" && coffeeDimMode
                      ? 800
                      : isDay
                        ? 500
                        : 0
                }
              >
                <Popup>
                  <div className="map-popup">
                    <strong className="map-popup-title">{p.name}</strong>
                    <br />
                    <em className={p.category === "coffee" ? "cat-coffee" : undefined}>
                      {p.category === "coffee" ? "☕ coffee" : p.category}
                    </em>
                    {pinMayHavePhoto(p) && <PlaceDetail pin={p} compact />}
                    {p.notes && (
                      <>
                        <br />
                        {p.notes}
                      </>
                    )}
                    {p.dayIds && p.dayIds.length > 0 && (
                      <>
                        <br />
                        {p.dayIds.slice(0, 4).map((d) => (
                          <button
                            key={d}
                            type="button"
                            className="popup-day-link"
                            onClick={() => onSelectDate(d)}
                          >
                            {d.slice(5)}
                          </button>
                        ))}
                      </>
                    )}
                  </div>
                </Popup>
              </Marker>
            );
          })}
          {quest.arrow && (
            <Marker
              key={`hop-arrow-${quest.arrow.lat.toFixed(5)}-${quest.arrow.lng.toFixed(5)}`}
              position={[quest.arrow.lat, quest.arrow.lng]}
              icon={arrowIcon(quest.arrow.bearing)}
              interactive={false}
              keyboard={false}
              zIndexOffset={1200}
            />
          )}
        </MapContainer>
      </div>

      <section className="card map-pin-list">
        <header className="panel-header">
          <h3>Pins ({visiblePins.length})</h3>
        </header>
        <ul className="pin-mini-list">
          {visiblePins.length === 0 && (
            <li className="muted">No pins for this filter.</li>
          )}
          {visiblePins.map((p) => {
            const coffeeDimmed = coffeeDimMode && p.category !== "coffee";
            return (
              <li
                key={p.id}
                className={[
                  pinMayHavePhoto(p) ? "has-photo" : "",
                  p.category === "coffee" ? "is-coffee" : "",
                  coffeeDimmed ? "is-dimmed" : "",
                ]
                  .filter(Boolean)
                  .join(" ") || undefined}
              >
                <CategoryIcon category={p.category} />
                <div>
                  <a
                    className="pin-name-link"
                    href={mapsLinks(p).primary}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <strong>{p.name}</strong>
                  </a>
                  {pinMayHavePhoto(p) && <PlaceDetail pin={p} compact />}
                  <p className="muted">
                    {p.category === "coffee" ? "☕ coffee" : p.category}
                    {p.notes ? ` · ${p.notes}` : ""}
                  </p>
                  {p.dayIds && p.dayIds.length > 0 && (
                    <div className="pin-day-links">
                      {p.dayIds.slice(0, 3).map((d) => (
                        <button
                          key={d}
                          type="button"
                          className="chip sm"
                          onClick={() => onSelectDate(d)}
                        >
                          {format(parseISO(d), "MMM d")}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}
